<script setup>
import { formatNumber } from '@/composables/useFormat'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

// pages/reports/attendance.vue — Addendum 08, Modul AD
// Status terlambat/tepat waktu di tabel ini SELALU dari field is_late yang
// dikirim backend (AttendanceReportService -- baca langsung dari kolom
// attendance_logs.is_late tersimpan saat clock-in, Modul AA). Halaman ini
// TIDAK menghitung ulang keterlambatan dengan cara apa pun.

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Attendance',
  },
})

const ability = useAbility()

// Patch — verifikasi manual (Bagian 2). Attendance.verify default
// Owner+Manager (lihat RolePermissionDefaults) -- kolom Terverifikasi &
// tombol Centang Semua/Verifikasi disembunyikan sama sekali untuk role
// yang aksinya pasti gagal di backend (sama prinsip dengan
// canManageAttendance di settings/[tab].vue).
const canVerify = computed(() => ability.can('verify', 'Attendance'))

// ── State ─────────────────────────────────────────────────────────────
const loading = ref(false)
const dateFrom = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
const dateTo = ref(new Date().toISOString().slice(0, 10))
const staffId = ref(null)
const staffList = ref([])

const rows = ref([])
const summary = ref({})

// ── Verifikasi manual (Bagian 2) ─────────────────────────────────────
// page/itemsPerPage dilacak manual (bukan andalkan show-select bawaan
// VDataTable) supaya "Centang Semua" bisa dihitung tepat ke baris yang
// SEDANG TAMPIL di halaman itu saja -- bukan seluruh `rows` (yang bisa
// berisi ratusan baris hasil filter tanggal, VDataTable baru memotongnya
// jadi per-halaman di sisi client).
const page = ref(1)
const itemsPerPage = ref(10)
const selectedIds = ref([])
const verifying = ref(false)

const currentPageIds = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value

  return rows.value.slice(start, start + itemsPerPage.value)
    .filter(r => !r.is_verified)
    .map(r => r.id)
})

const toggleSelect = (id, checked) => {
  if (checked) {
    if (!selectedIds.value.includes(id)) selectedIds.value = [...selectedIds.value, id]
  } else {
    selectedIds.value = selectedIds.value.filter(i => i !== id)
  }
}

const selectAllOnPage = () => {
  selectedIds.value = [...new Set([...selectedIds.value, ...currentPageIds.value])]
}

const verifySelected = async () => {
  if (!selectedIds.value.length) return

  verifying.value = true
  try {
    await useApi('/api/v1/attendance/verify').post({ body: { ids: selectedIds.value } })
    selectedIds.value = []
    await fetchReport()
  } catch (err) {
    console.error('❌ Gagal verifikasi kehadiran:', err)
  } finally {
    verifying.value = false
  }
}

// ── Dialog lihat foto penuh ──────────────────────────────────────────
const photoDialog = ref(false)
const photoDialogUrls = ref([])

const openPhotoDialog = item => {
  photoDialogUrls.value = [
    { label: 'Clock-in', url: item.clock_in_photo_url },
    { label: 'Clock-out', url: item.clock_out_photo_url },
  ].filter(p => p.url)
  photoDialog.value = true
}

// ── Staff dropdown (sama pola dengan attendance/schedules.vue) ─────────
const fetchStaffList = async () => {
  try {
    const response = await useApi('/api/v1/users', { query: { per_page: 100, is_active: 1 } }).get()
    const data = response?.data ?? response

    staffList.value = (data?.data ?? []).map(u => ({ title: u.name, value: u.id }))
  } catch (err) {
    console.error('❌ Gagal memuat daftar staff:', err)
  }
}

// ── Table Headers ────────────────────────────────────────────────────
const headers = [
  { title: 'Staff', key: 'staff_name' },
  { title: 'Jam Masuk', key: 'clock_in_at' },
  { title: 'Jam Keluar', key: 'clock_out_at' },
  { title: 'Durasi', key: 'duration_minutes', align: 'end' },
  { title: 'Status', key: 'status_label' },
  { title: 'Override', key: 'is_override' },
  { title: 'Foto', key: 'photo', sortable: false },
  { title: 'Terverifikasi', key: 'is_verified', sortable: false },
]

// ── Fetch ────────────────────────────────────────────────────────────
const fetchReport = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/reports/attendance', {
      query: {
        date_from: dateFrom.value || undefined,
        date_to: dateTo.value || undefined,
        user_id: staffId.value || undefined,
      },
    }).get()

    const data = response?.data ?? response

    rows.value = data?.rows ?? []
    summary.value = data?.summary ?? {}
  } catch (err) {
    console.error('❌ Attendance report error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStaffList()
  fetchReport()
})

// ── Export ───────────────────────────────────────────────────────────
const exportFile = format => {
  const params = new URLSearchParams({
    ...(dateFrom.value ? { date_from: dateFrom.value } : {}),
    ...(dateTo.value ? { date_to: dateTo.value } : {}),
    ...(staffId.value ? { user_id: staffId.value } : {}),
  })

  const url = `/api/v1/export/attendance/${format}?${params}`

  window.open(url, '_blank')
}

// ── Helpers ──────────────────────────────────────────────────────────
const formatDateTime = value => {
  if (!value) return '-'

  return new Date(value.replace(' ', 'T')).toLocaleString('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const statusColor = row => {
  if (row.is_late === true) return 'warning'
  if (row.is_late === false) return 'success'

  return 'secondary'
}
</script>

<template>
  <div>
    <VRow class="mb-4 align-center">
      <VCol
        cols="12"
        md="6"
      >
        <h4 class="text-h4 font-weight-medium mb-1">
          Laporan Kehadiran
        </h4>
        <p class="text-body-2 text-medium-emphasis">
          Rekap absen staff untuk keperluan penggajian
        </p>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            sm="4"
            md="3"
          >
            <VTextField
              v-model="dateFrom"
              type="date"
              label="Dari Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md="3"
          >
            <VTextField
              v-model="dateTo"
              type="date"
              label="Sampai Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md="3"
          >
            <VSelect
              v-model="staffId"
              :items="staffList"
              label="Staff (semua)"
              density="compact"
              clearable
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            md="1"
          >
            <VBtn
              color="primary"
              block
              :loading="loading"
              prepend-icon="ri-search-line"
              @click="fetchReport"
            >
              Cari
            </VBtn>
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VBtnGroup
              density="compact"
              variant="outlined"
              divided
              block
            >
              <VBtn
                prepend-icon="ri-file-excel-line"
                color="success"
                @click="exportFile('excel')"
              >
                Excel
              </VBtn>
              <VBtn
                prepend-icon="ri-file-list-3-line"
                color="info"
                @click="exportFile('csv')"
              >
                CSV
              </VBtn>
            </VBtnGroup>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VRow class="mb-4">
      <VCol
        cols="6"
        sm="4"
        md="2"
      >
        <VCard :loading="loading">
          <VCardText class="pa-3 text-center">
            <div class="text-caption text-medium-emphasis mb-1">
              Total Absen
            </div>
            <div class="text-h6 font-weight-bold">
              {{ formatNumber(summary.total_logs ?? 0) }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="6"
        sm="4"
        md="2"
      >
        <VCard :loading="loading">
          <VCardText class="pa-3 text-center">
            <div class="text-caption text-medium-emphasis mb-1">
              Tepat Waktu
            </div>
            <div class="text-h6 font-weight-bold text-success">
              {{ formatNumber(summary.total_on_time ?? 0) }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="6"
        sm="4"
        md="2"
      >
        <VCard :loading="loading">
          <VCardText class="pa-3 text-center">
            <div class="text-caption text-medium-emphasis mb-1">
              Terlambat
            </div>
            <div class="text-h6 font-weight-bold text-warning">
              {{ formatNumber(summary.total_late ?? 0) }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="6"
        sm="4"
        md="2"
      >
        <VCard :loading="loading">
          <VCardText class="pa-3 text-center">
            <div class="text-caption text-medium-emphasis mb-1">
              Override
            </div>
            <div class="text-h6 font-weight-bold text-info">
              {{ formatNumber(summary.total_override ?? 0) }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="6"
        sm="4"
        md="2"
      >
        <VCard :loading="loading">
          <VCardText class="pa-3 text-center">
            <div class="text-caption text-medium-emphasis mb-1">
              Total Durasi (menit)
            </div>
            <div class="text-h6 font-weight-bold">
              {{ formatNumber(summary.total_duration_minutes ?? 0) }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VCard :loading="loading">
      <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2 d-flex align-center justify-space-between flex-wrap gap-2">
        <span>Detail Kehadiran</span>
        <div v-if="canVerify" class="d-flex gap-2">
          <VBtn
            size="small"
            variant="tonal"
            prepend-icon="ri-checkbox-multiple-line"
            @click="selectAllOnPage"
          >
            Centang Semua
          </VBtn>
          <VBtn
            size="small"
            color="primary"
            prepend-icon="ri-shield-check-line"
            :disabled="!selectedIds.length"
            :loading="verifying"
            @click="verifySelected"
          >
            Verifikasi Terpilih ({{ selectedIds.length }})
          </VBtn>
        </div>
      </VCardTitle>
      <VDivider />
      <VDataTable
        v-model:page="page"
        :headers="headers"
        :items="rows"
        :items-per-page="itemsPerPage"
        :loading="loading"
        density="compact"
        hover
      >
        <template #item.clock_in_at="{ item }">
          {{ formatDateTime(item.clock_in_at) }}
        </template>
        <template #item.clock_out_at="{ item }">
          {{ formatDateTime(item.clock_out_at) }}
        </template>
        <template #item.duration_minutes="{ item }">
          {{ item.duration_minutes !== null ? `${item.duration_minutes} mnt` : '-' }}
        </template>
        <template #item.status_label="{ item }">
          <VChip
            size="small"
            :color="statusColor(item)"
            variant="tonal"
          >
            {{ item.status_label }}
          </VChip>
        </template>
        <template #item.is_override="{ item }">
          <VChip
            v-if="item.is_override"
            size="small"
            color="info"
            variant="tonal"
            prepend-icon="ri-shield-user-line"
          >
            {{ item.override_by_name ?? 'Override' }}
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >-</span>
        </template>
        <template #item.photo="{ item }">
          <VAvatar
            v-if="item.clock_in_photo_url || item.clock_out_photo_url"
            size="36"
            rounded
            style="cursor: pointer;"
            @click="openPhotoDialog(item)"
          >
            <VImg :src="item.clock_in_photo_url || item.clock_out_photo_url" cover />
          </VAvatar>
          <span
            v-else
            class="text-medium-emphasis"
          >-</span>
        </template>
        <template #item.is_verified="{ item }">
          <VCheckbox
            v-if="canVerify"
            :model-value="item.is_verified || selectedIds.includes(item.id)"
            :disabled="item.is_verified"
            hide-details
            density="compact"
            @update:model-value="val => toggleSelect(item.id, val)"
          />
          <VChip
            v-else
            size="small"
            :color="item.is_verified ? 'success' : 'secondary'"
            variant="tonal"
          >
            {{ item.is_verified ? 'Terverifikasi' : 'Belum' }}
          </VChip>
        </template>

        <template #no-data>
          <div class="text-center py-8">
            <VIcon
              icon="ri-fingerprint-line"
              size="48"
              color="secondary"
              class="mb-2"
            />
            <p class="text-body-2 text-medium-emphasis">
              Tidak ada data kehadiran untuk filter ini
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- Dialog lihat foto penuh -->
    <VDialog v-model="photoDialog" max-width="480">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>Foto Verifikasi Absen</span>
          <IconBtn @click="photoDialog = false">
            <VIcon icon="ri-close-line" />
          </IconBtn>
        </VCardTitle>
        <VCardText class="d-flex flex-column gap-4">
          <div v-for="photo in photoDialogUrls" :key="photo.label">
            <p class="text-caption text-medium-emphasis mb-1">{{ photo.label }}</p>
            <VImg :src="photo.url" rounded />
          </div>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
